import React, { ReactNode } from 'react';
import classnames from 'classnames';
import styles from './radio-button.css';

export interface RadioButtonProps {
  /** Radio button is selected */
  checked?: boolean;
  /** Label for the radio button */
  label?: string;
  /** The content to display inside the label if passed as children */
  children?: ReactNode;
  /** Adds required attribute */
  required?: boolean;
  /** Disables radio button disallowing interaction */
  disabled?: boolean;
  /** Adds additional classes to the component. */
  className?: string;
  /** Callback when radio button is clicked */
  onClick?: (e: React.FormEvent<HTMLInputElement>) => void;
  /** Name for input field */
  name?: string;
  /** Allows for input field to hold a value */
  value?: string | number;
  /** Id for the input field to link to the label */
  id: string;
  /** Allows for a boarder */
  border?: boolean;
  /** Highlight the label of the selected element */
  highlight?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Radio Button COmponent
 *
 * @param {RadioButtonProps} props
 *
 * @returns JSX.ELEMENT
 */
export const RadioButton = ({
  border,
  children,
  className,
  onClick,
  name,
  value,
  id,
  'data-testid': dataTestId,
  required,
  checked = false,
  disabled = false,
  highlight = false
}: RadioButtonProps) => {
  const radioClasses = classnames(styles.container, className, {
    [styles.checked]: checked
  });

  const labelClasses = classnames(styles.label, {
    [styles.disabled]: disabled,
    [styles.labelBorder]: border,
    [styles.highlight]: checked && highlight
  });

  return (
    <div className={radioClasses} {...(disabled ? { disabled } : {})} data-testid={dataTestId}>
      <input
        type="radio"
        className={styles.input}
        checked={checked}
        id={id}
        required={required}
        {...(!disabled && onClick ? { onChange: onClick } : {})}
        {...(name ? { name } : {})}
        {...(!disabled && typeof value !== 'undefined' ? { value } : {})}
        readOnly
      />
      <label className={labelClasses} htmlFor={id} data-testid={`label-${id}`}>
        <div className={styles.radio} />
        {children}
      </label>
    </div>
  );
};

export default RadioButton;
